export const ORDER_SUMMARY_COLUMNS = [
  {
    key: 'prescriptionnumber',
    label: 'Prescription ID',
    defaultOn: false,
    type: 'text',
  },
  { key: 'rx_no', label: 'Rx #', defaultOn: false, type: 'text' },
  { key: 'drug_name', label: 'Medication', defaultOn: true, type: 'text' },
  { key: 'qty', label: 'Quantity', defaultOn: true, type: 'text' },
  { key: 'prescriber', label: 'Prescriber', defaultOn: true, type: 'text' },
  { key: 'price', label: 'Amount due', defaultOn: false, type: 'money' },
  { key: 'ndc_code', label: 'NDC', defaultOn: false, type: 'text' },
  { key: 'fillnumber', label: 'Fill #', defaultOn: false, type: 'text' },
  {
    key: 'dispensed_qty',
    label: 'Dispensed qty',
    defaultOn: false,
    type: 'text',
  },
  { key: 'item_number', label: 'Item #', defaultOn: false, type: 'text' },
  {
    key: 'previous_price',
    label: 'Previous price',
    defaultOn: false,
    type: 'money',
  },
];

const COLUMN_BY_KEY = ORDER_SUMMARY_COLUMNS.reduce((acc, col) => {
  acc[col.key] = col;
  return acc;
}, {});

export const DEFAULT_SUMMARY_COLUMNS = ORDER_SUMMARY_COLUMNS.filter(
  (col) => col.defaultOn
).map((col) => col.key);

const SAMPLE_ITEMS = [
  {
    prescriptionnumber: 'RX10003421',
    rx_no: '10003421',
    drug_name: 'Amoxicillin 500mg Capsule',
    qty: 30,
    prescriber: 'Dr. Jane Smith',
    price: 4.2,
    ndc_code: '68180-0512-01',
    fillnumber: '1',
    dispensed_qty: 30,
    item_number: '51210',
    previous_price: 5.1,
  },
  {
    prescriptionnumber: 'RX10003422',
    rx_no: '10003422',
    drug_name: 'Lisinopril 10mg Tablet',
    qty: 90,
    prescriber: 'Dr. Jane Smith',
    price: 8.2,
    ndc_code: '00781-1506-92',
    fillnumber: '2',
    dispensed_qty: 90,
    item_number: '15069',
    previous_price: 9.4,
  },
];

const escapeHtml = (value) =>
  String(value == null ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const money = (value) => {
  const numeric = Number(value);
  const amount = Number.isFinite(numeric) ? numeric : 0;
  return `$${amount.toFixed(2)}`;
};

const knownColumn = (key) => !!COLUMN_BY_KEY[String(key || '').trim()];

const clampMask = (value) => {
  const numeric = parseInt(value, 10);
  if (!Number.isFinite(numeric) || numeric < 1) {
    return 0;
  }
  return Math.min(32, numeric);
};

export const htmlHidesAmountDue = (html) =>
  /data-hide-amount=["']?(1|true)["']?/i.test(String(html || ''));

export const htmlHasOrderSummary = (html) =>
  /data-card=["']summary["']/i.test(String(html || '')) ||
  /data-token=["']Order Summary["']/i.test(String(html || '')) ||
  /\{\{\s*Order Summary\s*\}\}/i.test(String(html || ''));

const attrValue = (html, name) => {
  const match = String(html || '').match(
    new RegExp(`${name}=["']([^"']*)["']`, 'i')
  );
  return match ? match[1] : '';
};

const defaultColumns = (hideAmount) =>
  hideAmount
    ? DEFAULT_SUMMARY_COLUMNS.filter((key) => key !== 'price')
    : DEFAULT_SUMMARY_COLUMNS.slice();

const parseColumnList = (raw, fallback) => {
  const seen = {};
  const keys = [];
  String(raw || '')
    .split(',')
    .forEach((part) => {
      const key = part.trim();
      if (!knownColumn(key) || seen[key]) {
        return;
      }
      seen[key] = true;
      keys.push(key);
    });
  if (!keys.length) {
    return fallback.slice();
  }
  return keys;
};

const parseMaskMap = (raw) => {
  const mask = {};
  String(raw || '')
    .split(',')
    .forEach((part) => {
      const idx = part.indexOf(':');
      if (idx < 1) return;
      const key = part.slice(0, idx).trim();
      if (!knownColumn(key)) return;
      const count = clampMask(part.slice(idx + 1));
      if (count > 0) {
        mask[key] = count;
      }
    });
  return mask;
};

export const parseSummaryConfig = (html) => {
  const source = String(html || '');
  const hideAmount = htmlHidesAmountDue(source);
  const listed = attrValue(source, 'data-summary-cols');
  let columns = listed
    ? parseColumnList(listed, defaultColumns(hideAmount))
    : defaultColumns(hideAmount);
  if (hideAmount) {
    columns = columns.filter((key) => key !== 'price');
  }
  if (!columns.length) {
    columns = defaultColumns(hideAmount);
  }
  return {
    columns,
    mask: parseMaskMap(attrValue(source, 'data-summary-mask')),
    hideAmount: columns.indexOf('price') === -1,
  };
};

export const serializeSummaryConfig = (config = {}) => {
  const hideAmount = Boolean(config.hideAmount);
  let columns = parseColumnList(
    (config.columns || []).join(','),
    defaultColumns(hideAmount)
  );
  if (hideAmount) {
    columns = columns.filter((key) => key !== 'price');
  }
  if (!columns.length) {
    columns = defaultColumns(hideAmount);
  }
  const mask = {};
  const incoming = config.mask || {};
  Object.keys(incoming).forEach((key) => {
    if (columns.indexOf(key) === -1) return;
    const count = clampMask(incoming[key]);
    if (count > 0) {
      mask[key] = count;
    }
  });
  return {
    columns,
    mask,
    hideAmount: columns.indexOf('price') === -1,
  };
};

const maskValue = (value, visibleCount) => {
  const text = String(value == null ? '' : value);
  const count = clampMask(visibleCount);
  if (!count || text.length <= count) {
    return text;
  }
  return `${text.slice(0, count)}${'*'.repeat(text.length - count)}`;
};

const formatColumnValue = (item, col, mask) => {
  if (!col) return '';
  let value = item[col.key];
  if (col.type === 'money') {
    value = value === '' || value == null ? '' : money(value);
  } else if (value == null) {
    value = '';
  }
  return maskValue(value, mask && mask[col.key]);
};

const summaryTextColor = (color) =>
  /^#[0-9a-fA-F]{6}$/.test(color || '') ? color : '#1e293b';

const orderSummaryCell = (content, options = {}) => {
  const align = options.align || 'left';
  const header = Boolean(options.header);
  const color = summaryTextColor(options.color);
  if (header) {
    return `<th align="${align}" style="padding:0 8px 10px 0;text-align:${align};font-family:Helvetica,Arial,sans-serif;font-size:14px;line-height:18px;font-weight:700;color:${color};border-bottom:1px solid #94a3b8;">${content}</th>`;
  }
  return `<td align="${align}" valign="top" style="padding:12px 8px 12px 0;text-align:${align};font-family:Helvetica,Arial,sans-serif;font-size:14px;line-height:20px;font-weight:400;color:${color};border-bottom:1px solid #cbd5e1;">${content}</td>`;
};

const alignFor = (col) => {
  if (!col) return 'left';
  if (col.key === 'qty' || col.key === 'dispensed_qty') return 'center';
  if (col.type === 'money') return 'right';
  return 'left';
};

const MODERN_SUMMARY_COLS = 'drug_name,qty,prescriber';

export const rewriteLegacyOrderSummaryCols = (html) =>
  String(html || '').replace(
    /<([a-z0-9]+)([^>]*data-card=["']summary["'][^>]*)>/gi,
    (full, tag, attrs) => {
      if (
        !/data-summary-cols=["']prescriptionnumber,rx_no,drug_name,qty,price["']/i.test(
          attrs
        )
      ) {
        return full;
      }
      let next = attrs.replace(
        /data-summary-cols=["'][^"']*["']/i,
        `data-summary-cols="${MODERN_SUMMARY_COLS}"`
      );
      if (/data-hide-amount=/i.test(next)) {
        next = next.replace(
          /data-hide-amount=["'][^"']*["']/i,
          'data-hide-amount="1"'
        );
      } else {
        next += ' data-hide-amount="1"';
      }
      return `<${tag}${next}>`;
    }
  );

export const buildSampleOrderSummaryHtml = (config = {}) => {
  const parsed = serializeSummaryConfig(config);
  let visible = parsed.columns
    .map((key) => COLUMN_BY_KEY[key])
    .filter(Boolean);
  if (!visible.length) {
    return '';
  }
  const prescriberCol = visible.filter((col) => col.key === 'prescriber')[0];
  if (
    prescriberCol &&
    !SAMPLE_ITEMS.some(
      (item) => item.prescriber != null && String(item.prescriber).trim()
    )
  ) {
    visible = visible.filter((col) => col.key !== 'prescriber');
  }
  const showAmount = parsed.columns.indexOf('price') !== -1;
  const total = SAMPLE_ITEMS.reduce(
    (sum, item) => sum + (Number(item.price) || 0),
    0
  );
  const color = summaryTextColor(config.textColor);
  const header = visible
    .map((col) =>
      orderSummaryCell(`<strong>${escapeHtml(col.label)}</strong>`, {
        header: true,
        align: alignFor(col),
        color,
      })
    )
    .join('');
  const body = SAMPLE_ITEMS.map((item) => {
    const cells = visible
      .map((col) =>
        orderSummaryCell(
          escapeHtml(formatColumnValue(item, col, parsed.mask)),
          {
            align: alignFor(col),
            bold: col.key === 'drug_name',
            color,
          }
        )
      )
      .join('');
    return `<tr>${cells}</tr>`;
  }).join('');
  const totalLabelIndex = visible.findIndex((item) => item.key !== 'price');
  const footer = showAmount
    ? `<tr>${visible
        .map((col, index) => {
          if (col.key === 'price') {
            return orderSummaryCell(
              `<strong>${escapeHtml(money(total))}</strong>`,
              { align: 'right', bold: true, color }
            );
          }
          if (index === totalLabelIndex) {
            return orderSummaryCell('<strong>Total</strong>', {
              bold: true,
              color,
            });
          }
          return orderSummaryCell('', { align: alignFor(col), color });
        })
        .join('')}</tr>`
    : '';
  return `
<table class="mcc-order-summary" role="presentation" border="0" cellpadding="0" cellspacing="0" width="100%" style="width:100%;border-collapse:collapse;margin:4px 0 8px;">
  <tr>
    ${header}
  </tr>
  ${body}
  ${footer}
</table>
`.trim();
};

export const buildSampleOrderSummaryText = (config = {}) => {
  const parsed = serializeSummaryConfig(config);
  const lines = SAMPLE_ITEMS.map((item) => {
    const parts = [];
    if (item.rx_no) {
      parts.push(`Rx ${item.rx_no}`);
    }
    parsed.columns.forEach((key) => {
      if (key === 'rx_no' || key === 'price' || key === 'previous_price') {
        return;
      }
      const value = item[key];
      if (value != null && value !== '') {
        parts.push(String(value));
      }
    });
    return parts.join(' ');
  }).filter(Boolean);
  if (parsed.columns.indexOf('price') !== -1 && !parsed.hideAmount) {
    lines.push('Total $12.40');
  }
  return lines.join('\n');
};

const applyAttrsToOpenTag = (openTag, attrs) => {
  let next = openTag;
  Object.keys(attrs).forEach((name) => {
    const value = attrs[name];
    const re = new RegExp(`\\s${name}=(?:"[^"]*"|'[^']*')`, 'i');
    if (re.test(next)) {
      next = next.replace(re, ` ${name}="${value}"`);
    } else {
      next = next.replace(/>$/, ` ${name}="${value}">`);
    }
  });
  return next;
};

const SUMMARY_MARKER = (config) =>
  `<div hidden data-hide-amount="${config.hideAmount ? '1' : '0'}" data-summary-cols="${config.columns.join(',')}" data-summary-mask="${Object.keys(
    config.mask
  )
    .map((key) => `${key}:${config.mask[key]}`)
    .join(',')}"></div>`;

export const setSummaryConfig = (html, nextConfig) => {
  const config = serializeSummaryConfig(nextConfig);
  const attrs = {
    'data-hide-amount': config.hideAmount ? '1' : '0',
    'data-summary-cols': config.columns.join(','),
    'data-summary-mask': Object.keys(config.mask)
      .map((key) => `${key}:${config.mask[key]}`)
      .join(','),
  };
  let source = String(html || '');
  const targetRe =
    /<[a-z0-9]+[^>]*(?:data-card=["']summary["']|data-hide-amount=|data-summary-cols=)[^>]*>/gi;
  if (targetRe.test(source)) {
    targetRe.lastIndex = 0;
    return source.replace(targetRe, (full) => applyAttrsToOpenTag(full, attrs));
  }
  return `${SUMMARY_MARKER(config)}${source}`;
};

export const setHideAmountDue = (html, hide) => {
  const current = parseSummaryConfig(html);
  let columns = current.columns.slice();
  const hasPrice = columns.indexOf('price') !== -1;
  if (hide && hasPrice) {
    columns = columns.filter((key) => key !== 'price');
  } else if (!hide && !hasPrice) {
    columns = parseColumnList(
      DEFAULT_SUMMARY_COLUMNS.concat(columns).join(','),
      DEFAULT_SUMMARY_COLUMNS
    );
  }
  return setSummaryConfig(html, { columns, mask: current.mask });
};

export const SAMPLE_ORDER_SUMMARY_HTML = buildSampleOrderSummaryHtml();
export const SAMPLE_ORDER_SUMMARY_HTML_NO_AMOUNT = buildSampleOrderSummaryHtml({
  hideAmount: true,
});
export const SAMPLE_ORDER_SUMMARY_TEXT = buildSampleOrderSummaryText({
  columns: DEFAULT_SUMMARY_COLUMNS.concat(['price']),
});
export const SAMPLE_ORDER_SUMMARY_TEXT_NO_AMOUNT = buildSampleOrderSummaryText({
  hideAmount: true,
});



// WEBPACK FOOTER //
// ./src/components/message-control-center/orderSummary.js